/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-nested-ternary */
import { FC, useEffect, useState, ChangeEvent, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from './EditPriceDrawer.module.css';
import { EditPriceDrawerProps } from './EditPriceDrawerProps.types';
import { t } from 'i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import { useUpdateProductPrice } from '@/services/menu/menus.service';
import ProductsTable from './EditPriceTabItems/ProductsTable';
import { useSTKContext } from '@/context/context';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import {
  getStores,
  isPriceInvalid,
  combineStoreGroupPrices,
  calculateValidPriceRange,
} from '@/services/menu/menus.helper.service';
import { InputStates } from '@/components/Input/Input.types';
import { Typography } from '@mui/material';
import Button from '@/components/Button/Button';
import { VariantPriceUpdateRequest } from '@/types/menus.types';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import { PriceChangeConfirmation } from '@/pages/Franchisee/Menus/EditPriceDrawer/PriceChangeConfirmation';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

function buildVariantPriceRequest(
  storeProducts: any[],
  inputPrice: string,
  selectedStores: string[],
): VariantPriceUpdateRequest[] {
  return storeProducts
    .filter((product) => {
      // Must have variantId
      if (!product.variantId) return false;

      // If product is not price locked, always allow
      if (!product.priceLock) return true;

      // If product is price locked, only allow if it's currently inactive
      // (allowing activation of inactive price-locked products)
      const hasActivePrice = product.storeGroups?.some((sg: any) => {
        const priceRange = sg.chosenPriceRange;
        return priceRange && priceRange !== 'Inactive' && priceRange.trim() !== '';
      });

      // Allow price-locked products only if they don't have active prices (are inactive)
      return !hasActivePrice;
    })
    .map((product) => ({
      stores: selectedStores,
      variantId: product.variantId,
      price: inputPrice.replace('$', ''),
    }));
}

export const EditPriceDrawer: FC<EditPriceDrawerProps> = ({
  isOpen,
  onClose,
  storeProducts,
  afterSave,
  setPriceDeviationsData,
}) => {
  const { storesAndGroups } = useSTKContext();
  const [state, setState] = useState(isOpen);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [inputPriceState, setInputPriceState] = useState(InputStates.Default);
  const [errorResponse, setErrorResponse] = useState<any | undefined>(undefined);
  const stores = getStores(storesAndGroups);
  const { alias } = useFranchiseeAlias();
  const {
    mutateAsync: updateProductPrice,
    data: updatePriceResponse,
    isLoading: isSavingPrice = false,
  } = useUpdateProductPrice();

  const [inputPrice, setInputPrice] = useState('');
  const [isPriceChangeConfirmationOpen, setIsPriceChangeConfirmationOpen] = useState<boolean>(false);
  useEffect(() => {
    if (!isOpen) {
      setInputPrice('');
      setErrorResponse(undefined);
    }
    setState(isOpen);
  }, [isOpen]);

  const currentStoreProducts = storeProducts;
  const data = combineStoreGroupPrices(currentStoreProducts, storesAndGroups);
  const tableData = data;
  const minValidPrices: number[] = [];
  const maxValidPrices: number[] = [];
  const storeNames = tableData.map((store) => store.storeGroupName);

  currentStoreProducts.forEach((sProduct) => {
    sProduct.storeGroups.forEach((sGroup) => {
      if (storeNames.includes(sGroup.storeGroupName) && storeNames.length > 0) {
        minValidPrices.push(sGroup?.minValidPrice);
        maxValidPrices.push(sGroup?.maxValidPrice);
      }
    });
  });

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputPrice(value);
  };

  const { lowestValidPrice, highestValidPrice } = calculateValidPriceRange(minValidPrices, maxValidPrices);

  const isValidPrice = useCallback((price: string) => {
    const numericPrice = parseFloat(price.replace('$', ''));
    return !isNaN(numericPrice) && numericPrice >= 0 && numericPrice <= 99.99;
  }, []);

  const processProduct = () => {
    // For FZ users (alias === null), validate against price range
    if (alias === null && isPriceInvalid(inputPrice, lowestValidPrice, highestValidPrice)) {
      setInputPriceState(InputStates.Error);
    } else if (alias !== null && !isValidPrice(inputPrice)) {
      // For Admin users, validate $0.00 - $99.99 range
      setInputPriceState(InputStates.Error);
    } else {
      const variantPriceUpdateRequest = buildVariantPriceRequest(storeProducts, inputPrice, stores);

      updateProductPrice(variantPriceUpdateRequest, {
        onSuccess: () => {
          setPriceIsSaved(true);
          afterSave();
        },
        onError: (error: any) => {
          // Handle 504 Gateway Timeout errors - request is still processing on backend
          if (
            error?.response?.status === 504 ||
            error?.code === 'ECONNABORTED' ||
            error?.message?.includes('timeout') ||
            error?.message?.includes('Gateway Timeout')
          ) {
            // Create a timeout response object that EditDrawerSuccessAndFailure can handle
            const timeoutResponse = {
              failedStores: [],
              status: 'TIMEOUT',
              message: t('requestProcessingModal.timeout.message'),
            };
            setErrorResponse(timeoutResponse);
            setPriceIsSaved(true);
            // Call afterSave to refresh data and clear selections even on timeout
            afterSave();
            return;
          }
          // Handle other errors (exceptions, backend errors, no response)
          // Create an error response object that EditDrawerSuccessAndFailure can handle
          const errorResponseObj = {
            // successStoresCount is missing/undefined - this triggers the allMenusFailedTitle display
            failedStores: [],
            status: 'ERROR',
            message: error?.message || t('editMenuPrice.errorMessage'),
          };
          setErrorResponse(errorResponseObj);
          setPriceIsSaved(true);
          // Call afterSave to refresh data and clear selections even on error
          afterSave();
        },
      });
    }
  };

  const onCloseDrawer = () => () => {
    setPriceIsSaved(false);
    setInputPriceState(InputStates.Default);
    setInputPrice('');
    setErrorResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer()} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer()}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.alertBanner}>
            <CalendarAlert />
          </div>
          <div className={styles.content}>
            {isSavingPrice ? (
              <EditDrawerProcessing count={stores.length} entityToUpdate={EntityToUpdate.MENU} />
            ) : priceIsSaved ? (
              <EditDrawerSuccessAndFailure
                apiResponse={errorResponse || updatePriceResponse}
                entityToUpdate={EntityToUpdate.MENU}
              />
            ) : (
              <>
                <div className={styles.productSettings}>
                  <strong>{t('editMenuPrice.editMenuPrice')}</strong>
                  <p className={styles.subtext1}>{t('editMenuPrice.activateInactiveProducts')}</p>
                  <MaskedInput
                    className={styles.input}
                    id="new-price"
                    label={t('editMenuPrice.newPricePoint')}
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    value={inputPrice}
                    onChange={handlePriceChange}
                    state={inputPriceState}
                  />
                  <Typography
                    mt={'2px'}
                    mb={'10px'}
                    sx={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: `${inputPriceState === InputStates.Error ? 'var(--red-700)' : 'var(--black-700)'}`,
                    }}
                  >
                    {t('editMenuPrice.priceRange')}: ${lowestValidPrice} - ${highestValidPrice}
                  </Typography>
                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.submitButton}
                      data-testid="edit-price"
                      disabled={
                        tableData.length === 0 ||
                        inputPrice === '' ||
                        (alias === null && isPriceInvalid(inputPrice, lowestValidPrice, highestValidPrice)) ||
                        (alias !== null && !isValidPrice(inputPrice))
                      }
                      onClick={() => setIsPriceChangeConfirmationOpen(true)}
                      type="button"
                    >
                      {t('editMenuPrice.continue')}
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <ProductsTable storeProducts={storeProducts} setPriceDeviationsData={setPriceDeviationsData} />
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
      <PriceChangeConfirmation
        isOpen={isPriceChangeConfirmationOpen}
        onClose={() => setIsPriceChangeConfirmationOpen(false)}
        onCancel={onCloseDrawer()}
        storeProducts={storeProducts}
        afterSave={processProduct}
        newPrice={inputPrice.replace('$', '')}
      />
    </div>
  );
};
